'use client';

import { Suspense, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useRouter, useSearchParams, usePathname } from '@/lib/router';
import { CaretLeftIcon, CaretRightIcon, CalendarIcon } from '@phosphor-icons/react';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { ExpenseTabBar } from '@/components/ExpenseTabBar';
import {
  parseMonthParam,
  formatMonthParam,
  capitalizeFirstLetter,
  getMonthYearLabel,
  EXPENSE_FILTER_MIN_DATE,
} from '@/lib/expense-utils';

function ExpensesLayoutInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const monthParam = searchParams.get('month');
  const selectedDate = useMemo(() => parseMonthParam(monthParam), [monthParam]);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return (
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth()
    );
  }, [selectedDate]);

  const isMinMonth = useMemo(() => {
    return (
      selectedDate.getFullYear() === EXPENSE_FILTER_MIN_DATE.getFullYear() &&
      selectedDate.getMonth() === EXPENSE_FILTER_MIN_DATE.getMonth()
    );
  }, [selectedDate]);

  const navigateMonth = useCallback((direction: -1 | 1) => {
    const newMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + direction, 1);

    if (direction === -1 && newMonth < EXPENSE_FILTER_MIN_DATE) return;
    if (direction === 1) {
      const now = new Date();
      const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      if (newMonth > endOfCurrentMonth) return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('month', formatMonthParam(newMonth));
    params.set('page', '1');
    params.delete('day');

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [selectedDate, searchParams, pathname, router]);

  return (
    <div className="animate-in space-y-6 pb-20 duration-500 fade-in">
      <PageHeader
        title="Fluxo de Caixa"
        description="Gestão histórica e faturas de cartão."
        actions={(
          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 shadow-sm">
            <button
              onClick={() => navigateMonth(-1)}
              disabled={isMinMonth}
              className={`p-1 rounded-lg transition-colors ${
                isMinMonth ? 'opacity-30 cursor-not-allowed' : 'hover:bg-surface-sunken'
              }`}
            >
              <CaretLeftIcon className="size-4 text-text-tertiary" />
            </button>
            <div className="flex items-center gap-2 px-2">
              <CalendarIcon className="size-4 text-text-tertiary" />
              <span className="min-w-[120px] text-center text-sm font-semibold text-text-secondary">
                {capitalizeFirstLetter(getMonthYearLabel(selectedDate))}
              </span>
            </div>
            <button
              onClick={() => navigateMonth(1)}
              disabled={isCurrentMonth}
              className={`p-1 rounded-lg transition-colors ${
                isCurrentMonth ? 'opacity-30 cursor-not-allowed' : 'hover:bg-surface-sunken'
              }`}
            >
              <CaretRightIcon className="size-4 text-text-tertiary" />
            </button>
          </div>
        )}
      />

      <Suspense>
        <ExpenseTabBar />
      </Suspense>

      <Outlet />
    </div>
  );
}

export default function ExpensesLayout() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ExpensesLayoutInner />
    </Suspense>
  );
}
